import React, { forwardRef, useCallback, useMemo } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
declare const i18next: any;

import classNames from 'classnames';
import { OverrideProps } from '../BaseComponent';
import { InputTextController, SelectController } from './controller';
import { FormFieldData, FormTypes, Width } from './types';

interface FormDataTypeMap<P = {}, D extends React.ElementType = 'div'> {
  props: P & {
    methods: UseFormReturn<any>;
    fields: FormFieldData[];
    actions?: React.ReactNode;
    onSubmitForm?: (v: any) => void;
  };
  defaultComponent: D;
}

export type FormDataProps<
  D extends React.ElementType = FormDataTypeMap['defaultComponent'],
  P = {},
> = OverrideProps<FormDataTypeMap<P, D>, D>;

interface FormDataDefaultProps {
  component: React.ElementType;
}

const defaultProps: FormDataDefaultProps = {
  component: 'div',
};

export const FormData = forwardRef((props: FormDataProps, ref: any) => {
  const { t } = i18next;
  const { methods, fields, onSubmitForm, actions } = {
    ...defaultProps,
    ...props,
  };

  const renderField = useCallback((field: FormFieldData) => {
    switch (field.type) {
      case FormTypes.INPUT:
        return <InputTextController {...field} />;
      case FormTypes.SELECT:
        return <SelectController {...field} />;
      default:
        return <></>;
    }
  }, []);

  const getWidthStyles = (width?: Width): string => {
    if (!width) return 'col-12';
  
    const breakpoints: (keyof Width)[] = ['sm', 'md', 'lg'];
  
    const classes = breakpoints
      .map((bp) => {
        const size = width[bp];
        if (size === undefined || size === '') return '';
        if (size === 'auto') return `col-${bp}-auto`;
        return `col-${bp}-${size}`;
      })
      .filter(Boolean);
  
    return classes.length > 0 ? classes.join(' ') : 'col';
  };

  const onSubmit = useMemo(() => {
    return !!onSubmitForm ? onSubmitForm : () => {};
  }, [onSubmitForm]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, () => {
          console.log('error');
        })}
      >
        <div className="row g-3">
          {fields
            .filter((item) => !item.hidden)
            .map((formField, index) => {
              if (formField?.rules) {
                if (!formField?.rules?.pattern) {
                  formField.rules.pattern = {
                    value: /^\S/,
                    message: t('PLEASE_REMOVE_LEADING_WHITE_SPACE'),
                  };
                }

                if (typeof formField?.rules?.maxLength === 'number') {
                  formField.rules.maxLength = {
                    value: formField?.rules?.maxLength,
                    message: t('RULES.MAX_LENGTH', {
                      value: formField?.rules?.maxLength,
                    }),
                  };
                }

                if (typeof formField?.rules?.minLength === 'number') {
                  formField.rules.minLength = {
                    value: formField?.rules?.minLength,
                    message: t('RULES.MIN_LENGTH', {
                      value: formField?.rules?.minLength,
                    }),
                  };
                }

                if (formField?.rules?.email) {
                  formField.rules.pattern = {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: t('RULES.WRONG_EMAIL_FORMAT'),
                  };
                }
              }

              return (
                <div key={index} className={getWidthStyles(formField?.width)}>
                  <div
                    className={classNames(formField?.className, {
                      // [styles.hidden]: formField.hidden,
                    })}
                  >
                    {renderField(formField)}
                  </div>
                </div>
              );
            })}
        </div>
      </form>
    </FormProvider>
  );
});

FormData.displayName = 'FormData';
export default FormData;
export * from './types';
